import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { toProjectType } from '@/features/projects/utils';
import {
  Command,
  CommandCollection,
  CommandDialog,
  CommandDialogPopup,
  CommandEmpty,
  CommandFooter,
  CommandGroup,
  CommandGroupLabel,
  CommandInput,
  CommandItem,
  CommandList,
  CommandPanel,
} from '@/ui/components/command';
import { projectsLiteQuery } from '@/features/projects/queries';
import { ProjectTypeIcon } from '@/features/projects/components/project-type-icon';
import { organizationWorkspacesQuery } from '@/features/workspaces/queries';
import { WorkspaceTypeIcon } from '@/features/workspaces/components/workspace-type-icon';
import { organizationKnowledgeBasesQuery } from '@/features/knowledge-base/queries';
import {
  ArrowDownIcon,
  ArrowTurnBackwardIcon,
  ArrowUpIcon,
  Folder02Icon,
  Search01Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/ui/components/tooltip';
import { Kbd, KbdCommand, KbdGroup } from '@/ui/components/kbd';
import { useNavigate } from '@tanstack/react-router';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { SidebarHeaderButton } from '@/ui/components/sidebar';
import { Icon } from '@/ui/components/icon';
import { useAtom } from 'jotai';
import { globalSearchOpenAtom } from '@/features/search/store';

type SearchItem = {
  value: string;
  label: string;
  sublabel?: string;
  icon: React.ReactNode;
  onClick: () => void;
};

type SearchGroup = {
  value: string;
  label: string;
  items: SearchItem[];
};

const COLLAPSED_GROUP_SIZE = 5;

export function GlobalSearch() {
  const navigate = useNavigate();
  const auth = useAuth();
  const organizationId = auth.user.organizationId;
  const [open, setOpen] = useAtom(globalSearchOpenAtom);
  const { data: projects } = useQuery(projectsLiteQuery(organizationId));
  const { data: workspaces } = useQuery(organizationWorkspacesQuery(organizationId));
  const { data: knowledgeBases } = useQuery(organizationKnowledgeBasesQuery(organizationId));
  const [searchText, setSearchText] = React.useState('');

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [setOpen]);

  const searchGroups: SearchGroup[] = [];
  const groupLimit = searchText ? undefined : COLLAPSED_GROUP_SIZE;

  if (workspaces && workspaces.length > 0) {
    searchGroups.push({
      value: 'workspaces',
      label: t({ en: 'Workspaces', nb: 'Arbeidsområder' }),
      items: workspaces.slice(0, groupLimit).map((workspace) => ({
        value: `workspace-${workspace.id}`,
        label: workspace.name,
        sublabel: workspace.projectName,
        icon: <WorkspaceTypeIcon className="shrink-0" type={workspace.type} />,
        onClick: () =>
          navigate({
            to: '/project/$projectId/workspaces/$workspaceId',
            params: { projectId: workspace.projectId, workspaceId: workspace.id },
          }),
      })),
    });
  }

  if (projects && projects.length > 0) {
    searchGroups.push({
      value: 'projects',
      label: t({ en: 'Projects', nb: 'Prosjekter' }),
      items: projects.slice(0, groupLimit).map((project) => ({
        value: `project-${project.id}`,
        label: project.name,
        icon: <ProjectTypeIcon className="shrink-0" type={toProjectType(project.type)} />,
        onClick: () => navigate({ to: '/project/$projectId', params: { projectId: project.id } }),
      })),
    });
  }

  if (knowledgeBases && knowledgeBases.length > 0) {
    searchGroups.push({
      value: 'knowledge-bases',
      label: t({ en: 'Knowledge base', nb: 'Kunnskapsbase' }),
      items: knowledgeBases.slice(0, groupLimit).map((kb) => ({
        value: `kb-${kb.id}`,
        label: kb.name,
        icon: <Icon icon={Folder02Icon} />,
        onClick: () =>
          navigate({
            to: '/settings/knowledge-base/internal/$knowledgeBaseId',
            params: { knowledgeBaseId: kb.id },
          }),
      })),
    });
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      onOpenChangeComplete={(open) => {
        if (!open) setSearchText('');
      }}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <SidebarHeaderButton
              aria-label="search"
              className="ml-auto"
              onClick={() => setOpen(true)}
            >
              <Icon icon={Search01Icon} />
            </SidebarHeaderButton>
          }
        />
        <TooltipPopup side="right">
          {t({ en: 'Search', nb: 'Søk' })}{' '}
          <KbdGroup>
            <KbdCommand />
            <Kbd>K</Kbd>
          </KbdGroup>
        </TooltipPopup>
      </Tooltip>
      <CommandDialogPopup>
        <Command items={searchGroups} open>
          <div className="relative">
            <CommandInput
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t({
                en: 'Search projects, workspaces...',
                nb: 'Søk prosjekter, arbeidsområder...',
              })}
            />
            <KbdGroup className="absolute inset-y-0 my-auto right-3">
              <KbdCommand />
              <Kbd>K</Kbd>
            </KbdGroup>
          </div>
          <CommandPanel>
            <CommandEmpty className={'break-all'}>
              {t({
                en: `No results found for "${searchText}"`,
                nb: `Ingen resultater funnet for "${searchText}"`,
              })}
            </CommandEmpty>
            <CommandList>
              {(group: SearchGroup) => (
                <CommandGroup key={group.value} items={group.items}>
                  <CommandGroupLabel>{group.label}</CommandGroupLabel>
                  <CommandCollection>
                    {(item: SearchItem) => (
                      <CommandItem
                        key={item.value}
                        onClick={() => {
                          item.onClick();
                          setOpen(false);
                        }}
                        value={item.value}
                      >
                        {item.icon}
                        <span className="line-clamp-1">{item.label}</span>
                        {item.sublabel && (
                          <span className="ml-auto shrink-0 pl-2 text-muted text-xs">
                            {item.sublabel}
                          </span>
                        )}
                      </CommandItem>
                    )}
                  </CommandCollection>
                </CommandGroup>
              )}
            </CommandList>
          </CommandPanel>
          <CommandFooter>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <KbdGroup>
                  <Kbd>
                    <Icon icon={ArrowUpIcon} />
                  </Kbd>
                  <Kbd>
                    <Icon icon={ArrowDownIcon} />
                  </Kbd>
                </KbdGroup>
                <span>{t({ en: 'Navigate', nb: 'Navigere' })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Kbd>
                  <Icon icon={ArrowTurnBackwardIcon} />
                </Kbd>
                <span>{t(common.open)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Kbd>Esc</Kbd>
              <span>{t(common.close)}</span>
            </div>
          </CommandFooter>
        </Command>
      </CommandDialogPopup>
    </CommandDialog>
  );
}
